import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import {
  FiTrendingUp,
  FiBarChart2,
  FiTarget,
  FiCalendar,
  FiZap,
  FiAward,
  FiRefreshCw,
  FiCheckCircle,
  FiStar,
  FiChevronRight,
} from "react-icons/fi";

const HabitFeatureShowcase = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const progressControls = useAnimation();

  const features = [
    {
      id: 1,
      icon: <FiTarget />,
      title: "Smart Progress Tracking",
      description:
        "Visual progress bars with AI-generated motivational quotes that adapt to your current achievement level.",
      highlight: "85% of users report increased consistency",
      color: "#9281dd",
      gradient: "linear-gradient(135deg, #9281dd, #6d5bd4)",
    },
    {
      id: 2,
      icon: <FiCalendar />,
      title: "Flexible Scheduling",
      description:
        "Choose from daily habits, weekly goals, or specific day routines. Your habits, your schedule.",
      highlight: "7 different habit types supported",
      color: "#b6aaec",
      gradient: "linear-gradient(135deg, #b1a6e5, #8c7edd)",
    },
    {
      id: 3,
      icon: <FiBarChart2 />,
      title: "Deep Analytics",
      description:
        "Advanced insights into your habit patterns, streak performance, and success rates over time.",
      highlight: "Predictive streak maintenance",
      color: "#3b48f6",
      gradient: "linear-gradient(135deg, #7447c7, #3a38b8)",
    },
    {
      id: 4,
      icon: <FiZap />,
      title: "Streak Protection",
      description:
        "Never break your streak again. Get smart reminders and motivation when you need it most.",
      highlight: "Avg. streak increase: 40%",
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B, #D97706)",
    },
  ];

  useEffect(() => {
    const sequence = async () => {
      await progressControls.start({
        width: ["0%", "45%", "78%", "92%", "100%"],
        transition: {
          duration: 4,
          ease: "easeInOut",
          times: [0, 0.3, 0.6, 0.8, 1],
        },
      });
      await progressControls.start({
        width: "78%",
        transition: { duration: 0.5 },
      });
    };
    sequence();

    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [progressControls]);

  const handleFeatureClick = (index) => {
    setActiveFeature(index);
  };

  return (
    <ShowcaseContainer>
      <ContentWrapper>
        <HeaderSection>
          <Badge>
            <FiStar />
            <span>SMART HABIT TRACKING</span>
          </Badge>
          <MainTitle>
            Build Better
            <GradientText>Habits</GradientText>
            Daily
          </MainTitle>
          <Subtitle>
            Our intelligent habit tracker adapts to your lifestyle with
            personalized motivation, flexible scheduling, and deep insights to
            ensure lasting change.
          </Subtitle>
        </HeaderSection>

        <ShowcaseGrid>
          {/* Feature Navigation - Now properly stacked on mobile */}
          <FeatureNav>
            {features.map((feature, index) => (
              <FeatureNavItem
                key={feature.id}
                active={activeFeature === index}
                onClick={() => handleFeatureClick(index)}
                color={feature.color}
                gradient={feature.gradient}
                whileHover={{ x: 5 }}
              >
                <NavIcon
                  active={activeFeature === index}
                  gradient={feature.gradient}
                >
                  {feature.icon}
                </NavIcon>
                <NavContent>
                  <NavTitle>{feature.title}</NavTitle>
                  <NavHighlight>{feature.highlight}</NavHighlight>
                </NavContent>
                {activeFeature === index && (
                  <ActiveIndicator
                    layoutId="activeIndicator"
                    gradient={feature.gradient}
                  />
                )}
              </FeatureNavItem>
            ))}
          </FeatureNav>

          {/* Main Feature Display */}
          <FeatureDisplay>
            <FeatureVisual>
              {/* Animated Progress Bar Demo */}
              <ProgressDemo>
                <ProgressHeader>
                  <ProgressTitle>Morning Meditation</ProgressTitle>
                  <ProgressStats>
                    <StreakBadge gradient={features[activeFeature].gradient}>
                      <FiZap />
                      <span>14 day streak</span>
                    </StreakBadge>
                    <ProgressPercent
                      gradient={features[activeFeature].gradient}
                    >
                      78%
                    </ProgressPercent>
                  </ProgressStats>
                </ProgressHeader>

                <ProgressContainer>
                  <AnimatedProgressBar
                    animate={progressControls}
                    gradient={features[activeFeature].gradient}
                  />
                  <ProgressMilestones>
                    {[25, 50, 75, 100].map((milestone) => (
                      <Milestone key={milestone}>
                        <MilestoneDot
                          achieved={milestone <= 78}
                          color={features[activeFeature].color}
                        />
                        <MilestoneLabel>{milestone}%</MilestoneLabel>
                      </Milestone>
                    ))}
                  </ProgressMilestones>
                </ProgressContainer>

                <MotivationQuote>
                  "You're on fire! Maintaining a 14-day streak shows incredible
                  dedication. Keep this momentum going! 🔥"
                </MotivationQuote>
              </ProgressDemo>

              {/* Floating Stats - Hidden on mobile for better experience */}
              <FloatingStats>
                <StatCard
                  style={{ top: "10%", left: "5%" }}
                  gradient={features[activeFeature].gradient}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <StatIcon>🔥</StatIcon>
                  <StatValue>14</StatValue>
                  <StatLabel>Day Streak</StatLabel>
                </StatCard>
                <StatCard
                  style={{ top: "60%", right: "5%" }}
                  gradient={features[activeFeature].gradient}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <StatIcon>📊</StatIcon>
                  <StatValue>92%</StatValue>
                  <StatLabel>Success Rate</StatLabel>
                </StatCard>
                <StatCard
                  style={{ bottom: "10%", left: "30%" }}
                  gradient={features[activeFeature].gradient}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <StatIcon>⚡</StatIcon>
                  <StatValue>40%</StatValue>
                  <StatLabel>Faster Progress</StatLabel>
                </StatCard>
              </FloatingStats>
            </FeatureVisual>

            <FeatureContent>
              <FeatureBadge gradient={features[activeFeature].gradient}>
                {features[activeFeature].icon}
                <span>FEATURE {activeFeature + 1}</span>
              </FeatureBadge>

              <FeatureTitle>{features[activeFeature].title}</FeatureTitle>

              <FeatureDescription>
                {features[activeFeature].description}
              </FeatureDescription>

              <FeatureHighlights>
                <HighlightItem>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>AI-generated motivational quotes</span>
                </HighlightItem>
                <HighlightItem>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>Customizable progress milestones</span>
                </HighlightItem>
                <HighlightItem>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>Real-time streak tracking</span>
                </HighlightItem>
                <HighlightItem>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>Detailed analytics dashboard</span>
                </HighlightItem>
              </FeatureHighlights>

              <CTASection>
                <CTAText>Ready to transform your habits?</CTAText>
                <CTAButton
                  gradient={features[activeFeature].gradient}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Start Your Success</span>
                  <FiChevronRight />
                </CTAButton>
              </CTASection>
            </FeatureContent>
          </FeatureDisplay>
        </ShowcaseGrid>
      </ContentWrapper>
    </ShowcaseContainer>
  );
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const ShowcaseContainer = styled.section`
  padding: 8rem 8%;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 5%;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: rgba(146, 129, 221, 0.1);
  border: 1px solid rgba(146, 129, 221, 0.3);
  border-radius: 100px;
  color: #9281dd;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 2rem;
`;

const MainTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 300;
  line-height: 1.2;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
  font-family: "Montserrat", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #9281dd, #6d5bd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
  margin: 0 8px;
  font-weight: 400;
  font-size: 4rem;

  @media (max-width: 768px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    padding: 0 1rem;
  }
`;

const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  margin-bottom: 6rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

// UPDATED: FeatureNav now stacks properly on mobile
const FeatureNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  /* Desktop: normal column layout */
  @media (min-width: 1025px) {
    max-width: 400px;
  }

  /* Tablet: still column but with adjustments */
  @media (max-width: 1024px) and (min-width: 769px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.75rem;

    /* Make feature items take 50% width on tablet */
    & > * {
      flex: 1 1 calc(50% - 0.75rem);
      min-width: 0;
    }
  }

  /* Mobile: full-width column stack */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;

    & > * {
      width: 100%;
      max-width: 100%;
    }
  }
`;

const FeatureNavItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: ${({ active }) => (active ? "#ffffff" : "transparent")};
  border: 1px solid
    ${({ active, gradient }) =>
      active ? "rgba(146, 129, 221, 0.2)" : "rgba(0, 0, 0, 0.1)"};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: ${({ active }) =>
    active ? "0 10px 40px rgba(146, 129, 221, 0.1)" : "none"};

  &:hover {
    background: #ffffff;
    transform: translateX(5px);
    box-shadow: 0 10px 30px rgba(146, 129, 221, 0.1);
    border-color: rgba(146, 129, 221, 0.3);
  }

  ${({ active }) =>
    active &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: ${({ gradient }) =>
          gradient || "linear-gradient(90deg, #9281dd, #6d5bd4)"};
      }
    `}

  /* Mobile-specific adjustments */
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 0.75rem;

    &:hover {
      transform: translateX(3px);
    }
  }

  /* Tablet adjustments */
  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 1rem;
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;

    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const NavIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: ${({ active, gradient }) =>
    active
      ? gradient || "linear-gradient(135deg, #9281dd, #6d5bd4)"
      : "rgba(146, 129, 221, 0.1)"};
  color: ${({ active }) => (active ? "#ffffff" : "#9281dd")};
  transition: all 0.3s ease;
  flex-shrink: 0;

  /* Mobile adjustments */
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  /* Tablet adjustments */
  @media (max-width: 1024px) and (min-width: 769px) {
    width: 56px;
    height: 56px;
    font-size: 1.75rem;
  }
`;

const NavContent = styled.div`
  flex: 1;
  min-width: 0; /* Allows text truncation on small screens */

  /* Tablet adjustments - hide highlight text */
  @media (max-width: 1024px) and (min-width: 769px) {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const NavTitle = styled.div`
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 0.25rem;
  font-size: 1.125rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.125rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 1rem;
    line-height: 1.2;
  }
`;

const NavHighlight = styled.div`
  font-size: 0.875rem;
  color: #718096;

  /* Hide on mobile for cleaner look */
  @media (max-width: 768px) {
    display: none;
  }

  /* Hide on tablet too */
  @media (max-width: 1024px) and (min-width: 769px) {
    display: none;
  }
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  right: 1.25rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(135deg, #9281dd, #6d5bd4)"};

  /* Hide on mobile for cleaner look */
  @media (max-width: 1024px) {
    display: none;
  }
`;

const FeatureDisplay = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const FeatureVisual = styled.div`
  position: relative;

  @media (max-width: 768px) {
    margin: 0 -1rem;
  }
`;

const ProgressDemo = styled.div`
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(146, 129, 221, 0.1),
    0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 30px 80px rgba(146, 129, 221, 0.15),
      0 15px 40px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    padding: 1.5rem;
    margin: 0 1rem;
    box-shadow: 0 10px 40px rgba(146, 129, 221, 0.1),
      0 5px 15px rgba(0, 0, 0, 0.05);
  }
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const ProgressTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ProgressStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 1rem;
  }
`;

const StreakBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(135deg, #F59E0B, #D97706)"};
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: 100px;

  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
  }
`;

const ProgressPercent = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(135deg, #9281dd, #6d5bd4)"};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ProgressContainer = styled.div`
  margin: 2rem 0;

  @media (max-width: 768px) {
    margin: 1.5rem 0;
  }
`;

const AnimatedProgressBar = styled(motion.div)`
  height: 12px;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(90deg, #9281dd, #6d5bd4)"};
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const ProgressMilestones = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const Milestone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MilestoneDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ achieved, color }) =>
    achieved ? color || "#9281dd" : "rgba(0, 0, 0, 0.1)"};
  margin-bottom: 0.25rem;
  box-shadow: ${({ achieved, color }) =>
    achieved ? `0 0 10px ${color || "#9281dd"}40` : "none"};
`;

const MilestoneLabel = styled.span`
  font-size: 0.75rem;
  color: #718096;
`;

const MotivationQuote = styled.div`
  font-style: italic;
  color: #4a5568;
  padding: 1.5rem;
  background: rgba(146, 129, 221, 0.05);
  border-radius: 12px;
  margin-top: 2rem;
  border-left: 3px solid #9281dd;
  line-height: 1.6;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-top: 1.5rem;
    font-size: 0.875rem;
  }
`;

const FloatingStats = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  /* Hidden on mobile for better experience */
  @media (max-width: 768px) {
    display: none;
  }
`;

const StatCard = styled(motion.div)`
  position: absolute;
  background: white;
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 16px;
  padding: 1rem;
  min-width: 120px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(146, 129, 221, 0.1);
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${() => Math.random() * 2}s;
  pointer-events: auto;
  cursor: pointer;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ gradient }) =>
      gradient || "linear-gradient(135deg, #9281dd, #6d5bd4)"};
    border-radius: 16px 16px 0 0;
  }
`;

const StatIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const FeatureContent = styled.div`
  animation: ${fadeInUp} 0.6s ease-out;

  @media (max-width: 1024px) {
    order: -1; /* Bring content above visual on mobile */
  }
`;

const FeatureBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(135deg, #9281dd, #6d5bd4)"};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  border-radius: 100px;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const FeatureDescription = styled.p`
  font-size: 1.125rem;
  line-height: 1.7;
  color: #4a5568;
  margin-bottom: 2.5rem;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const FeatureHighlights = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #1a1a2e;
  font-weight: 500;

  span {
    flex: 1;
    font-size: 1rem;

    @media (max-width: 768px) {
      font-size: 0.9375rem;
    }
  }
`;

const CheckIcon = styled.div`
  color: #10b981;
  font-size: 1.25rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const CTASection = styled.div`
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding-top: 1.5rem;
  }
`;

const CTAText = styled.div`
  font-size: 1.125rem;
  color: #4a5568;
  margin-bottom: 1.5rem;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const CTAButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(135deg, #9281dd, #6d5bd4)"};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(146, 129, 221, 0.3);

  &:hover {
    box-shadow: 0 20px 40px rgba(146, 129, 221, 0.4);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem 2rem;
    font-size: 1rem;
  }
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3rem;
  background: white;
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 24px;
  margin-top: 4rem;
  box-shadow: 0 10px 40px rgba(146, 129, 221, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
    padding: 2rem;
    margin-top: 3rem;
    border-radius: 16px;
  }
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(135deg, #9281dd, #6d5bd4)"};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StatDescription = styled.div`
  font-size: 0.875rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(0, 0, 0, 0.1),
    transparent
  );

  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(0, 0, 0, 0.1),
      transparent
    );
  }
`;

export default HabitFeatureShowcase;
